<template src="./templates/index.html" />

<script type="text/ecmascript-6">
import isArray from 'lodash/isArray'
import isEqual from 'lodash/isEqual'
import {  mapGetters } from 'vuex'
import Logo from 'components/icons/logo/'
import SubmissionMethods from 'src/pages/submissions/script/common.methods'
import Misc, {scrollToSection} from 'src/utils/helper/misc'
import isEmpty from 'lodash/isEmpty'
import { MdIconCompany, MdIconResources, MdIconComplianceExecution, MdTooltip } from '@antiqua-dls/vue-components'

import StarIcon from './icons/star-icon'
import FoldersIcon from './icons/folders-icon'
import UploadIcon from './icons/upload-icon'
import BundleIcon from '../fou-navigation/icons/bundle-icon'
import SignAgreementModal from '../small-popup-info-modal/index.vue'


export default {
  name: 'SiteNavigationFooter',

  components: {
    Logo,
    StarIcon,
    FoldersIcon,
    UploadIcon,
    BundleIcon,
    MdIconCompany,
    MdIconResources,
    MdIconComplianceExecution,
    MdTooltip,
    SignAgreementModal
  },

  data () {
    return {
      menu: false,
      removeSubscriptions: null,
      permissionArr: [],
    }
  },

  computed: {
    user: {
      get () {
        return this.$store.getters['auth/user']
      },
      set (user) {
        this.user.name = user.name
        this.user.displayName = user.displayName
      }
    },
    ...mapGetters('auth', ['hasRole', 'isContractLocked']),
    ...mapGetters('dashboard', {
      showAgreementModal: 'getShowAgreementModal'
    }),
    isValidNavigation: {
      get () {
        let navigations = ['get_started', 'sign_up', 'application_status', 'success_signup', 'success_signup_status']
        return !navigations.includes(this.getCurrentPage())
      }
    },
    checkNewUser: {
      get () {
        if (this.hasRole(['foundryAdmin', 'foundryUser', 'foundryFontManager', 'foundrySalesManager'])) {
          return true
        }
        return this.hasRole(['foundrySupport', 'bulkIngestionUser']);
      }
    },
    checkSupportUser() {
      return !(this.user.authenticated && this.isValidNavigation && !this.hasRole(['foundrySupport']))
    },
    ...mapGetters('auth', {
      userPermissions: 'getPermissionsArr'
    }),
    ...mapGetters('sideNavigation', ['showHamIcon']),
    ...mapGetters('profile',{
      sessionFoundryId: ['sessionFoundryId'],
      defaultFoundry: ['defaultFoundry'],
      foundries: ['foundries'],
      foundryBannerURL: ['getBannerURL'],
      foundryLogoURL: ['getLogoURL'],
    }),
    filteredFoundryContract: {
      get() {
        const foundry = this.foundries.find(f => f.id === this.sessionFoundryId);
        return foundry && (foundry?.contractSummary === 'UFDA 2' || foundry?.contractSummary === 'Monotype IP');
      }
    },
    hasFoundryProfilePermission() {
      return Misc.foundryHasModulePermission('Foundry Profile', this.foundries, this.sessionFoundryId)
    }
  },

  created () {
    this.setSubscriptions()
  },
  

  beforeUnmount () {
    if (this.removeSubscriptions) {
      this.removeSubscriptions()
    }
  },

  methods: {
    ...SubmissionMethods,
    ...Misc,
    setMenu() {
      this.menu = false
    },
    setSubscriptions () {
      this.removeSubscriptions = this.$store.subscribe((mutation, state) => {
        if (mutation.type === 'profile/set') {
          this.$store.dispatch('auth/checkAuth')
            .then(() => {
              this.user = this.$store.getters['auth/user'] // get refreshed display name
            })
            .catch(() => {})
        }
      })
    },
    createNewSubmission() {
      this.closeSideNav()
      return new Promise(resolve => {
        const sessionId = window.localStorage.getItem('sessionFoundryId')
        const foundryData = this.setFoundryForModuleOnNavigation(this.defaultFoundry, this.foundries, 'submissions', sessionId)
        window.localStorage.setItem('permissionsArr', JSON.stringify(Misc.getPermissionsArr(this.foundries)))
        this.$store.dispatch('auth/setPermissionsArr', Misc.getPermissionsArr(this.foundries))
        this.$store.dispatch('auth/setSessionFoundry', { id: foundryData.foundryId, name: foundryData.foundryName }).then(() => {
          this.createSubmission().then(submission => {
            this.$router.push({
              name: 'submissions_id',
              params: {
                id: submission?.id
              }
            })
            resolve(submission)
          })
        })
      })
    },
    createNewBundle() {
      this.closeSideNav()
      return new Promise(resolve => {
        const sessionId = window.localStorage.getItem('sessionFoundryId')
        const foundryData = this.setSessionFoundryForBundleModuleOnNavigation(this.defaultFoundry, this.foundries, 'bundles', sessionId, false)
        window.localStorage.setItem('permissionsArr', JSON.stringify(Misc.getPermissionsArr(this.foundries)))
        this.$store.dispatch('auth/setPermissionsArr', Misc.getPermissionsArr(this.foundries))
        this.$store.dispatch('auth/setSessionFoundry', { id: foundryData.foundryId, name: foundryData.foundryName }).then(() => {
          this.$store.dispatch('bundle/createBundle', { foundries: [{ name: foundryData.foundryName, id: foundryData.foundryId }] })
            .then(res => {
              this.$router.push({
                name: 'BundleSubmissionDraft',
                params: {
                  id: res?.bundle?.id
                }
              })
            })
        })
      })
    },

    navigateToVideos() {
      this.$store.commit('dashboard/setScrollToArticles', true);
      this.closeSideNav()
      this.$router.push({ name: 'dashboard' })
      if (this.filteredFoundryContract) {
        this.$nextTick(() => {
          // Scroll to the articles section after the page is rendered
          scrollToSection('.fou-dashboard-page-articles')
        });
      } else {
        this.$store.commit('dashboard/setShowAgreementModal', true)
      }
    },
    navigateToAgreementSection() {
      this.$router.push({ name: 'contract' })
    },

    brandYourProfile() {
      this.closeSideNav()
      const sessionFoundryId = window.localStorage.getItem('sessionFoundryId');
      this.$router.push(`/edit-foundry/${sessionFoundryId}`);
    },
    
    getCurrentPage() {
      const routeName = this.$route.name
      return routeName
        ? this.$sanitize(routeName)
        : false
    },
    isValidPermissionArr() {
      if (!isEmpty(this.userPermissions)) {
        this.permissionArr = this.userPermissions || JSON.parse(window.localStorage.getItem('permissionsArr'))
      }
      return isArray(this.permissionArr) && !isEqual(this.permissionArr.length, 0)
    },
    getNavigationTabsPermission(tabName) {
      return this.permissionArr.includes(tabName)
    },
    closeSideNav() {
      this.$store.dispatch('sideNavigation/changeHamIconStatus', false)
    }
  },

  
}
</script>

<style lang="scss" src="./stylesheets/index.scss" module></style>
